/**
 * Clown.art — Animations runtime
 * --------------------------------------------------------------
 * 1. IntersectionObserver pour ajouter `.is-visible` aux éléments
 *    avec `.clown-reveal` ou `.clown-reveal-stagger` quand ils entrent
 *    dès leur entrée dans le viewport.
 * 2. Parallax léger sur `[data-clown-parallax]` au scroll
 *    (rAF throttle, désactivé si prefers-reduced-motion).
 *
 * Wrappé en IIFE, safe à charger en defer. Ne touche pas le DOM
 * tant que DOMContentLoaded n'est pas fired.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ============================================================
     1) Reveal au scroll
     ============================================================ */
  function initReveal() {
    var revealEls = document.querySelectorAll('.clown-reveal, .clown-reveal-stagger, .clown-reveal--fade, .clown-reveal--zoom, .clown-reveal--left, .clown-reveal--right');
    if (!revealEls.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      revealEls.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.remove('is-pending');
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      // A shrunken root can leave short final blocks hidden at maximum scroll.
      // Reveal on entry, including large containers and end-of-page content.
      rootMargin: '0px',
      threshold: 0
    });

    // Read positions before writing classes to avoid repeated layout work.
    var positions = Array.prototype.map.call(revealEls, function (el) {
      return { element: el, top: el.getBoundingClientRect().top };
    });
    positions.forEach(function (item) {
      // Only content below the viewport can become hidden. This also preserves
      // visible content when restoring a scrolled page or opening an anchor URL.
      if (item.top < window.innerHeight) {
        item.element.classList.add('is-visible');
      } else {
        item.element.classList.add('is-pending');
        observer.observe(item.element);
      }
    });
  }

  /* ============================================================
     2) Parallax léger
     ============================================================ */
  function initParallax() {
    if (prefersReducedMotion) return;

    var parallaxEls = Array.prototype.slice.call(document.querySelectorAll('[data-clown-parallax]'));
    if (!parallaxEls.length) return;

    var ticking = false;
    var viewportH = window.innerHeight;

    function updateParallax() {
      parallaxEls.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        // Skip si l'élément n'est pas visible (perf)
        if (rect.bottom < -100 || rect.top > viewportH + 100) return;
        var intensity = parseFloat(el.dataset.clownParallax) || 0.12;
        // Centre de l'élément vs centre du viewport
        var elementCenter = rect.top + rect.height / 2;
        var viewportCenter = viewportH / 2;
        var offset = (elementCenter - viewportCenter) * intensity;
        el.style.setProperty('--clown-parallax-y', (-offset).toFixed(2) + 'px');
      });
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    function onResize() {
      viewportH = window.innerHeight;
      updateParallax();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    updateParallax();
  }

  /* ============================================================
     Bootstrap
     ============================================================ */
  function bootstrap() {
    initReveal();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
})();
